/**
 * Admin Lead Management — Membership Leads (RestroPulse Guest Hub).
 *
 * Route:  /admin/loyalty/leads
 *
 * Lists every membership lead captured via the NFC hub. Supports
 * outlet + status filtering, inline status updates and CSV export.
 * Bound to the existing `loyalty` feature flag so it stays hidden
 * for tenants without the loyalty module.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Download, Users, RefreshCw } from 'lucide-react';

const STATUS_OPTIONS = ['new', 'contacted', 'converted', 'closed'];
const STATUS_STYLES = {
  new:       'bg-blue-100 text-blue-800 border-blue-200',
  contacted: 'bg-amber-100 text-amber-800 border-amber-200',
  converted: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  closed:    'bg-gray-100 text-gray-700 border-gray-200',
};

export default function AdminMembershipLeads() {
  const { API, getHeaders } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outletFilter, setOutletFilter] = useState('__all__');
  const [statusFilter, setStatusFilter] = useState('__all__');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (outletFilter && outletFilter !== '__all__') params.outlet_id = outletFilter;
      if (statusFilter && statusFilter !== '__all__') params.status = statusFilter;
      const [outRes, leadRes] = await Promise.all([
        outlets.length === 0
          ? axios.get(`${API}/admin/outlets`, { headers: getHeaders() })
          : Promise.resolve({ data: outlets }),
        axios.get(`${API}/admin/membership-leads`, {
          headers: getHeaders(), params,
        }),
      ]);
      if (outlets.length === 0) setOutlets(outRes.data || []);
      setLeads(leadRes.data.leads || []);
    } catch {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders, outletFilter, statusFilter, outlets]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (leadId, status) => {
    try {
      const res = await axios.patch(
        `${API}/admin/membership-leads/${leadId}/status`,
        { status },
        { headers: getHeaders() },
      );
      setLeads((prev) => prev.map((l) => (l.id === leadId ? res.data : l)));
      toast.success(`Status updated to ${status}`);
    } catch {
      toast.error('Could not update status');
    }
  };

  const downloadCsv = async () => {
    try {
      const params = {};
      if (outletFilter && outletFilter !== '__all__') params.outlet_id = outletFilter;
      if (statusFilter && statusFilter !== '__all__') params.status = statusFilter;
      const res = await axios.get(`${API}/admin/membership-leads.csv`, {
        headers: getHeaders(), params, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `membership_leads_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not export CSV');
    }
  };

  const counts = useMemo(() => {
    const c = { new: 0, contacted: 0, converted: 0, closed: 0 };
    leads.forEach((l) => { c[l.status] = (c[l.status] || 0) + 1; });
    return c;
  }, [leads]);

  return (
    <div className="p-6 space-y-4" data-testid="admin-membership-leads">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2 text-gray-900">
            <Users className="w-6 h-6" /> Membership leads
          </h1>
          <p className="text-sm text-gray-600 mt-0.5">
            Captured via the NFC Guest Hub. Phone is the natural key — repeat submissions update in
            place.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" onClick={load}
            data-testid="leads-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-1.5" />Refresh
          </Button>
          <Button
            size="sm" onClick={downloadCsv}
            data-testid="leads-export-csv"
          >
            <Download className="w-4 h-4 mr-1.5" />Export CSV
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="leads-kpis">
        {STATUS_OPTIONS.map((s) => (
          <Card key={s} className="border-l-4"
                style={{ borderLeftColor:
                  s === 'new' ? '#2563eb' :
                  s === 'contacted' ? '#d97706' :
                  s === 'converted' ? '#059669' : '#6b7280' }}>
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500 capitalize">{s}</div>
              <div className="text-2xl font-semibold text-gray-900 mt-1"
                   data-testid={`kpi-${s}`}>
                {counts[s] || 0}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3" data-testid="leads-filters">
        <div className="min-w-[220px]">
          <Select value={outletFilter} onValueChange={setOutletFilter}>
            <SelectTrigger data-testid="filter-outlet">
              <SelectValue placeholder="All outlets" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All outlets</SelectItem>
              {outlets.map((o) => (
                <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Leads table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-left text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Phone</th>
                  <th className="px-4 py-3 font-medium">Outlet</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Captured</th>
                  <th className="px-4 py-3 font-medium">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-testid="leads-table-body">
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">Loading…</td></tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-gray-500"
                        data-testid="leads-empty">
                      No leads yet. Once guests submit interest via the NFC hub, they show up here.
                    </td>
                  </tr>
                ) : leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">{l.name || <span className="text-gray-400">—</span>}</td>
                    <td className="px-4 py-3 font-mono text-gray-700">{l.phone}</td>
                    <td className="px-4 py-3 text-gray-700">{l.outlet_name || '—'}</td>
                    <td className="px-4 py-3">
                      <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                        <SelectTrigger className="h-8 w-[140px]"
                                       data-testid={`lead-status-${l.id}`}>
                          <SelectValue>
                            <Badge className={`${STATUS_STYLES[l.status]} border capitalize`}>
                              {l.status}
                            </Badge>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((s) => (
                            <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {formatDate(l.created_at)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{l.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
